<template>
  <h2 v-if="isTitleVisible" class="right-table__title">
    {{ $t('hui-yuan-tao-can-quan-yi-dui-bi') }}
  </h2>
  <div class="right-table" v-if="columns.length">
    <div class="right-table__header" :class="{ 'right-table__header--sticky': enableStickyPanel }">
      <div class="cell cell--head cell--title">
        {{ $t('hui-yuan-quan-yi-dui-bi') }}
      </div>
      <div v-for="col in columns" :key="col.type" class="cell cell--head">
        <div class="plan-name">{{ col.name }}</div>
        <RightBtn :right-type="col.type" :text="col.name" v-if="enablePaymentButton" />
      </div>
    </div>

    <div class="right-table__body">
      <template v-for="group in tableGroups" :key="group.title">
        <div class="row row--group">
          <div class="cell cell--group">{{ group.title }}</div>
        </div>
        <div v-for="item in group.items" :key="item.title" class="row">
          <div class="cell cell--title">{{ item.title }}</div>
          <div
            v-for="col in columns"
            :key="`${group.title}-${item.title}-${col.type}`"
            class="cell cell--content"
          >
            <template v-if="displayValue(item.map[col.type]) === true">
              <IconCheck class="icon ok" />
            </template>
            <template v-else-if="displayValue(item.map[col.type]) === false">
              <IconCloseSmall class="icon err" />
            </template>
            <template v-else>
              <template v-if="extractLinkFromValue(item.map[col.type])">
                <Link target="_blank" :type="'info'" @click="handleContactClick">
                  {{ extractLinkFromValue(item.map[col.type])!.text }}
                </Link>
              </template>
              <template v-else>
                <span class="text">{{ displayValue(item.map[col.type]) }}</span>
              </template>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div v-else class="right-table__empty" />
</template>

<script setup lang="ts">
import { PositionCode, showDynamicContact } from '@rendora/common/features/contact';
import { getI18nService } from '@rendora/common/services/i18n';
import { useRightsStore, type IRightsCompareFirstLevelGroup } from '@rendora/common/store/rights';
import { IRightsType } from '@rendora/common/types/enums';
import IconCheck from '@rendora/design/assets/check.svg?component';
import IconCloseSmall from '@rendora/design/assets/close-small.svg?component';
import Link from '@rendora/design/components/link/Link.vue';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted } from 'vue';
import RightBtn from './RightBtn.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

const props = withDefaults(
  defineProps<{
    isTitleVisible?: boolean;
    visibleColumns?: IRightsType[];
    enableStickyPanel?: boolean;
    enablePaymentButton?: boolean;
    stickyTop?: string;
  }>(),
  {
    isTitleVisible: true,
    visibleColumns: undefined,
    enableStickyPanel: true,
    enablePaymentButton: true,
    stickyTop: '170px',
  },
);

const rightsStore = useRightsStore();
const { rightsDisplay, rightsCompare } = storeToRefs(rightsStore);
// 所有套餐的名称映射，便于表头展示
const typeToName = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {};
  (rightsDisplay.value?.rights || []).forEach(r => {
    map[r.rights_type as unknown as number] = r.rights_name;
  });
  // 确保企业定制存在名称（可能只作为展示占位）
  if (!map[IRightsType.ORG_CUSTOM]) {
    map[IRightsType.ORG_CUSTOM] = $t('qi-ye-ban');
  }
  return map;
});

// 当前需要对比的列（套餐类型）—支持通过 visibleColumns 配置展示哪些列
const columns = computed<{ type: IRightsType; name: string }[]>(() => {
  // 如果传入了 visibleColumns，使用传入的配置；否则使用默认列
  const result: IRightsType[] = props.visibleColumns ?? [
    IRightsType.TASTE,
    IRightsType.PERSONAL_LITE,
    IRightsType.PERSONAL,
    IRightsType.ORG_CUSTOM,
  ];
  const columns = result
    .filter(t => typeToName.value[t as unknown as number])
    .map(t => ({ type: t, name: typeToName.value[t as unknown as number] }));
  return columns;
});

// 计算 grid-template-columns：第一列固定宽度，后面的列等比自适应
const gridTemplateColumns = computed(() => {
  const colCount = columns.value.length + 1; // +1 是第一列
  return `188px ${'1fr '.repeat(colCount - 1).trim()}`;
});

// 将各套餐的分组数据规整为：
// [ { title, items: [ { title, map: { [type]: content } } ] } ]
const tableGroups = computed(() => {
  const res: Array<{
    title: string;
    items: Array<{ title: string; map: Record<number, unknown> }>;
  }> = [];

  const targetTypes = columns.value.map(c => c.type);
  if (targetTypes.length === 0) return res;

  // 构建 type -> groupTitle -> itemTitle -> content 的索引，便于快速查找
  const index: Record<number, Record<string, Record<string, unknown>>> = {};
  targetTypes.forEach(t => {
    const groups = rightsCompare.value?.[t] || ([] as IRightsCompareFirstLevelGroup[]);
    const gIndex: Record<string, Record<string, unknown>> = {};
    groups.forEach(g => {
      const iIndex: Record<string, unknown> = {};
      g.items.forEach(i => {
        // 每个 i.contents 目前只含单个内容
        const content = i.contents[0];
        iIndex[i.title] = content;
      });
      gIndex[g.title] = iIndex;
    });
    index[t as unknown as number] = gIndex;
  });

  // 汇总所有 group 和 item 的并集，以第一个类型的顺序为基准
  const baseType = targetTypes[0];
  const baseGroups = rightsCompare.value?.[baseType] || ([] as IRightsCompareFirstLevelGroup[]);
  // 过滤掉第一个分组
  baseGroups.slice(1).forEach(bg => {
    const rowItems: Array<{ title: string; map: Record<number, unknown> }> = [];

    // 收集此分组下所有出现过的 item 标题（包含其它列的并集）
    const titles = new Set<string>();
    titlesForType(targetTypes, index, bg.title).forEach(t => titles.add(t));

    titles.forEach(title => {
      const map: Record<number, unknown> = {};
      targetTypes.forEach(t => {
        const content = index[t as unknown as number]?.[bg.title]?.[title];
        map[t as unknown as number] = content ?? undefined;
      });
      rowItems.push({ title, map });
    });

    res.push({ title: bg.title, items: rowItems });
  });

  return res;
});

// 取一个分组下，所有列的条目标题并集，保持出现顺序（以各自顺序追加）
function titlesForType(
  types: IRightsType[],
  idx: Record<number, Record<string, Record<string, unknown>>>,
  groupTitle: string,
): string[] {
  const list: string[] = [];
  const seen = new Set<string>();
  types.forEach(t => {
    const items = idx[t as unknown as number]?.[groupTitle] || {};
    Object.keys(items).forEach(k => {
      if (!seen.has(k)) {
        seen.add(k);
        list.push(k);
      }
    });
  });
  return list;
}

// 将内容值转换成展示值
function displayValue(value: unknown): boolean | string {
  if (typeof value === 'undefined' || value === null) return '';
  // @ts-expect-error 结构定义为 IRightsCompareContent
  const v = (value && value.value) as unknown;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v || '';
  return '';
}

// 从字符串中提取 a 标签（可无 href）与其纯文本内容
function extractLinkFromValue(value: unknown): { href?: string; text: string } | null {
  const content = displayValue(value);
  if (typeof content !== 'string') return null;
  if (!content.includes('<a')) return null;

  // 优先匹配带 href 的情况
  const anchorMatch = content.match(/<a\b([^>]*)>([\s\S]*?)<\/a>/i);
  if (!anchorMatch) return null;

  const attrPart = anchorMatch[1] || '';
  const innerRaw = anchorMatch[2] || '';
  // 提取 href（可选）
  const hrefMatch = attrPart.match(/href=["']([^"']+)["']/i);
  const href = hrefMatch ? hrefMatch[1] : undefined;
  // 去除内部可能存在的标签，保留纯文本
  const innerText = innerRaw
    .replace(/<[^>]+>/g, '')
    .replace(',', '，') // 英文逗号换中文
    .trim();
  if (!innerText) return null;
  return href ? { href, text: innerText } : { text: innerText };
}

function handleContactClick() {
  showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
}

// 简化的滚动监听方案
onMounted(() => {
  nextTick(() => {
    const handleScroll = () => {
      const header = document.querySelector('.right-table__header--sticky');
      if (!header) return;

      // 获取表头元素的位置信息
      const headerRect = header.getBoundingClientRect();

      // 检查是否处于sticky状态（当元素的top位置等于我们设置的sticky top值时）
      if (headerRect.top <= parseInt(props.stickyTop)) {
        header.classList.add('box-shadow');
      } else {
        header.classList.remove('box-shadow');
      }
    };

    // 初始调用一次以设置正确状态
    handleScroll();

    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll);

    // 保存事件处理函数以便在卸载时移除
    (window as any)._rightTableScrollHandler = handleScroll;
  });
});

onUnmounted(() => {
  // 清理事件监听
  if ((window as any)._rightTableScrollHandler) {
    window.removeEventListener('scroll', (window as any)._rightTableScrollHandler);
    delete (window as any)._rightTableScrollHandler;
  }
});
</script>

<style scoped lang="scss">
h2 {
  width: 100%;
  text-align: center;
  margin-top: 12px;
  font-size: 14px;
  font-weight: var(--x-weight-medium);
}

.right-table {
  width: 100%;
  background: var(--x-white-1);
  border-radius: var(--x-radius-medium);
}
.right-table__header,
.row {
  display: grid;
  grid-template-columns: v-bind('gridTemplateColumns');
}
.right-table__header {
  background: var(--x-back-2);
  border-bottom: 1px solid var(--x-back-4);
}
.right-table__header--sticky {
  position: sticky;
  top: v-bind('stickyTop');
  z-index: 2;
}
.box-shadow {
  box-shadow: 0px 2px 20px 0px rgba(18, 25, 38, 0.1);
}
.cell {
  word-break: break-all;
  padding: 12px 16px;
  border-right: 1px solid var(--x-back-4);
}
.cell--title {
  max-width: 188px;
}
.cell:last-child {
  border-right: none;
}
.cell--head {
  display: block;
  text-align: center;
  padding: 16px 16px;
  font-weight: var(--x-weight-medium);
}
.cell--title {
  color: var(--x-text-1);
  font-weight: var(--x-weight-medium);
  display: flex;
  align-items: center;
}
.cell--content {
  text-align: center;
}
.row + .row {
  border-top: 1px solid var(--x-back-4);
}
.row--group {
  background: var(--x-back-2);
}
.row--group .cell--group {
  grid-column: 1 / -1;
  font-weight: 600;
}
.icon {
  width: 20px;
  height: 20px;
  margin: 0 auto;
}
.ok {
  color: var(--x-success-6);
}
.err {
  color: var(--x-error-6);
}
.plan-name {
  white-space: nowrap;
  text-align: center;
  margin-bottom: 10px;
}
.right-table__title {
  margin-bottom: 16px;
}
</style>
